"use client";

import { useEffect } from "react";

import { getIsAndroid } from "@/utils/getIsAndroid";

/** Words stay on the native IME path so their composition isn't disturbed. */
const NON_SYMBOL_REGEX = /[\p{L}\p{N}\s]/u;

export function isReplayableSymbol(data: string | null): data is string {
  return !!data && data.length === 1 && !NON_SYMBOL_REGEX.test(data);
}

/** The math input's typing rules (e.g. "2" + "2" → 22) all run on keydown. */
export const MATH_INPUT_ATTR = "data-math-input";

/** Spaces are left out: the math input's change handler splits the expression on them. */
function isReplayableTypedChar(target: HTMLElement, data: string | null): data is string {
  if (target.matches(`[${MATH_INPUT_ATTR}]`)) {
    return !!data && data.length === 1 && data !== " ";
  }
  return isReplayableSymbol(data);
}

/** Gboard composes without keydowns, so auto-pick never runs for "x+" or "+y". */
export function isReplayableComposedChar(char: string, charBefore: string | null): boolean {
  return (
    char.length === 1 &&
    char !== " " &&
    (isReplayableSymbol(char) || isReplayableSymbol(charBefore))
  );
}

const US_LAYOUT_CODES = [
  "Backquote",
  "Digit1",
  "Digit2",
  "Digit3",
  "Digit4",
  "Digit5",
  "Digit6",
  "Digit7",
  "Digit8",
  "Digit9",
  "Digit0",
  "Minus",
  "Equal",
  "BracketLeft",
  "BracketRight",
  "Backslash",
  "Semicolon",
  "Quote",
  "Comma",
  "Period",
  "Slash",
];
const US_LAYOUT_KEYS = "`1234567890-=[]\\;',./";
const US_LAYOUT_SHIFTED_KEYS = '~!@#$%^&*()_+{}|:"<>?';

/** Guards that match on `code` (e.g. `keyMatcher("Period", e)`) need it on replayed keys too. */
export function getUsLayoutKeyInit(key: string): Pick<KeyboardEventInit, "code" | "shiftKey"> {
  if (/^[a-z]$/i.test(key)) {
    return { code: `Key${key.toUpperCase()}`, shiftKey: key !== key.toLowerCase() };
  }

  const index = US_LAYOUT_KEYS.indexOf(key);
  if (index !== -1) {
    return { code: US_LAYOUT_CODES[index], shiftKey: false };
  }

  const shiftedIndex = US_LAYOUT_SHIFTED_KEYS.indexOf(key);
  if (shiftedIndex !== -1) {
    return { code: US_LAYOUT_CODES[shiftedIndex], shiftKey: true };
  }

  return {};
}

let isReplaying = false;

/** Dispatches `key` as a keydown on `target`; returns whether a handler consumed it. */
export function replayKeydown(target: EventTarget, key: string): boolean {
  const keydown = new KeyboardEvent("keydown", {
    key,
    ...getUsLayoutKeyInit(key),
    bubbles: true,
    cancelable: true,
    composed: true,
  });
  isReplaying = true;
  try {
    target.dispatchEvent(keydown);
  } finally {
    isReplaying = false;
  }
  return keydown.defaultPrevented;
}

let expectedRecommit: { target: EventTarget; char: string } | null = null;

/**
 * IME (Input Method Editor) is the on-screen keyboard, e.g. Gboard. When our handler already
 * inserted a char Gboard was still holding as an unfinished word, Gboard inserts it a second time
 * once React clears the input; this drops that duplicate.
 */
export function cancelImeRecommit(target: EventTarget, char: string) {
  expectedRecommit = { target, char };
}

function takeExpectedRecommit(e: InputEvent): boolean {
  const expected = expectedRecommit;
  expectedRecommit = null;
  return (
    !!expected &&
    e.target === expected.target &&
    e.inputType === "insertText" &&
    e.data === expected.char
  );
}

/** Android keyboards send `key: "Unidentified"`, so key-driven editor behavior never runs. */
export function installAndroidSymbolKeydownReplay(root: Document) {
  let lastKeyWasUnidentified = false;

  const handleKeyDown = (e: KeyboardEvent) => {
    lastKeyWasUnidentified = e.key === "Unidentified" || e.keyCode === 229;
    // Gboard's duplicate follows its own "Unidentified" keydown, so only a real key press ends it.
    if (!lastKeyWasUnidentified && !isReplaying) {
      expectedRecommit = null;
    }
  };

  const clearExpectedRecommit = () => {
    expectedRecommit = null;
  };

  const handleBeforeInput = (e: InputEvent) => {
    const replay = lastKeyWasUnidentified;
    lastKeyWasUnidentified = false;

    if (takeExpectedRecommit(e)) {
      e.preventDefault();
      return;
    }

    const { target } = e;
    if (
      !replay ||
      e.inputType !== "insertText" ||
      e.isComposing ||
      !(target instanceof HTMLElement) ||
      !target.closest(".ProseMirror") ||
      !isReplayableTypedChar(target, e.data)
    ) {
      return;
    }

    if (replayKeydown(target, e.data)) {
      e.preventDefault();
    }
  };

  const clearingEvents = ["focusout", "compositionstart", "compositionend"];

  root.addEventListener("keydown", handleKeyDown, { capture: true });
  root.addEventListener("beforeinput", handleBeforeInput, { capture: true });
  for (const type of clearingEvents) {
    root.addEventListener(type, clearExpectedRecommit, { capture: true });
  }

  return () => {
    expectedRecommit = null;
    root.removeEventListener("keydown", handleKeyDown, { capture: true });
    root.removeEventListener("beforeinput", handleBeforeInput, { capture: true });
    for (const type of clearingEvents) {
      root.removeEventListener(type, clearExpectedRecommit, { capture: true });
    }
  };
}

export function AndroidSymbolKeydownReplay() {
  useEffect(() => {
    if (!getIsAndroid()) {
      return;
    }
    return installAndroidSymbolKeydownReplay(document);
  }, []);

  return null;
}
